/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React, {createContext, useContext, useEffect, useLayoutEffect, useRef, useState, useCallback} from 'react';

/**
 * ThemeContext provides theme management for the application.
 *
 * Phase 2: Design System Foundation - Implements dark mode via CSS variables.
 * Supports three theme modes:
 * - 'light': Force light theme
 * - 'dark': Force dark theme
 * - 'system': Follow system preference (default)
 *
 * Preference rules:
 * - With nothing stored, the preference is 'system'.
 * - Only setThemePreference() writes localStorage, so only an explicit choice in the theme
 *   switcher or user menu changes the saved preference.
 * - Classic UI and the login route always render light. That override is applied
 *   per route and never touches storage, so the saved choice is back when the user
 *   returns to Preview UI, including after logout and login.
 *
 * Theme is applied via [data-theme] attribute on document root.
 */

const THEME_STORAGE_KEY = 'nexus-theme-preference';
const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

const ThemeContext = createContext({
  themePreference: THEMES.SYSTEM,
  activeTheme: THEMES.LIGHT,
  setThemePreference: () => {}
});

/**
 * Gets the current system color scheme preference.
 * @returns {'light' | 'dark'}
 */
function getSystemTheme() {
  if (typeof window === 'undefined') {
    return THEMES.LIGHT;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? THEMES.DARK
    : THEMES.LIGHT;
}

/**
 * Gets the stored theme preference from localStorage.
 * @returns {'light' | 'dark' | 'system'}
 */
function getStoredTheme() {
  // Same default as the browser path, so server-side and client rendering agree.
  if (typeof window === 'undefined') {
    return THEMES.SYSTEM;
  }
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && Object.values(THEMES).includes(stored)) {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to read theme preference from localStorage:', error);
  }
  // Nothing valid stored: follow the OS. Classic UI still renders light because
  // the route check below overrides the resolved theme, not the preference.
  return THEMES.SYSTEM;
}

/**
 * Stores the theme preference to localStorage. Only setThemePreference() calls this, so it
 * records explicit user choices and nothing else.
 * @param {string} theme
 */
function storeTheme(theme) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (error) {
    console.warn('Failed to save theme preference to localStorage:', error);
  }
}

/**
 * Applies the theme to the document root and body.
 * Also syncs Radix UI theme classes/attributes for portaled components (Select, Dialog, etc.)
 * @param {string} theme - 'light' or 'dark'
 */
function applyTheme(theme) {
  if (typeof document === 'undefined') {
    return;
  }
  // Apply our custom theme attribute for CSS variables
  document.documentElement.setAttribute('data-theme', theme);
  
  // Sync Radix UI theme for portaled components (Select, Dialog, etc.)
  // Radix portals render at body level and need these to inherit the correct theme
  
  // 1. Set Radix appearance classes on body
  if (theme === THEMES.DARK) {
    document.body.classList.add('dark');
    document.body.classList.remove('light');
  } else {
    document.body.classList.add('light');
    document.body.classList.remove('dark');
  }
  
  // 2. Ensure body has radix-themes class so Radix CSS selectors work
  document.body.classList.add('radix-themes');
  
  // 3. Set data-* attributes that Radix may use for theming
  document.body.setAttribute('data-is-root-theme', 'true');
  document.body.setAttribute('data-accent-color', 'green');
  document.body.setAttribute('data-gray-color', 'slate');
  document.body.setAttribute('data-radius', 'medium');
}

/**
 * The one place the rendered theme is worked out. Render and the hashchange listener
 * both call this, so they can't drift apart.
 * @param {'light' | 'dark' | 'system'} preference - Saved theme preference
 * @param {'light' | 'dark'} systemTheme - Current OS color scheme
 * @param {boolean} darkCapableRoute - Whether the current route supports dark mode
 * @returns {'light' | 'dark'}
 */
function resolveActiveTheme(preference, systemTheme, darkCapableRoute) {
  if (!darkCapableRoute) {
    return THEMES.LIGHT;
  }
  return preference === THEMES.SYSTEM ? systemTheme : preference;
}

/**
 * Dark mode only applies on Preview UI routes. Classic UI (ExtJS/RSC) and the login
 * route have no dark styles, so they always render light.
 * @param {string} hash - window.location.hash
 */
function isDarkCapableRoute(hash) {
  return hash.includes('preview/') && !hash.includes('login');
}

function currentHash() {
  return typeof window !== 'undefined' ? window.location.hash : '';
}

export function ThemeProvider({ children }) {
  // Three inputs, one derived output. Apps pass activeTheme to Radix <Theme appearance>,
  // so it has to be light on Classic and login, not just the [data-theme] attribute.
  const [themePreference, setThemePreferenceState] = useState(getStoredTheme);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const [darkCapableRoute, setDarkCapableRoute] = useState(() => isDarkCapableRoute(currentHash()));

  const activeTheme = resolveActiveTheme(themePreference, systemTheme, darkCapableRoute);

  // The hashchange listener subscribes once and reads the preference from here.
  const themePreferenceRef = useRef(themePreference);
  useLayoutEffect(() => {
    themePreferenceRef.current = themePreference;
  }, [themePreference]);

  // useLayoutEffect (not useEffect) so `data-theme` on <html> and the
  // `dark`/`radix-themes` classes on <body> are set BEFORE the first paint
  // on a browser refresh. Using useEffect leaves those unset for one paint,
  // during which var(--color-surface) resolves through Radix's light default
  // while the dark logo is already rendered -- the "gray mask" flash on the
  // global header until the next hash change re-runs styles.
  useLayoutEffect(() => {
    applyTheme(activeTheme);
  }, [activeTheme]);

  // Track the route. The DOM is updated straight away, before React re-renders, so
  // Classic UI and login never paint a frame in dark. The preference in memory is
  // the source of truth here; storage is not re-read, so a failed write can't undo
  // the user's choice on the next navigation.
  useLayoutEffect(() => {
    function onHashChange() {
      const darkCapable = isDarkCapableRoute(window.location.hash);
      applyTheme(resolveActiveTheme(themePreferenceRef.current, getSystemTheme(), darkCapable));
      setDarkCapableRoute(darkCapable);
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Keep the OS color scheme current. Cheap to always listen, and it means a switch
  // to System never has to catch up on a change it missed.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    function handleChange(e) {
      setSystemTheme(e.matches ? THEMES.DARK : THEMES.LIGHT);
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  const setThemePreference = useCallback((newTheme) => {
    if (!Object.values(THEMES).includes(newTheme)) {
      console.warn(`Invalid theme: ${newTheme}. Must be one of: ${Object.values(THEMES).join(', ')}`);
      return;
    }

    setThemePreferenceState(newTheme);
    storeTheme(newTheme);
  }, []);

  const value = {
    themePreference,     // Saved choice: 'light', 'dark' or 'system'. Pickers read this.
    activeTheme,         // What renders on this route: always 'light' or 'dark'.
    setThemePreference   // Save a new choice. The only code that writes storage.
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export { THEMES };
export default ThemeContext;
